import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  MoneyOff as MoneyOffIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";

function Row({ user, onDebtCleared }) {
  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, lessonId: null, type: null });
  const [partialDialog, setPartialDialog] = useState({ open: false });
  const [selectedLessons, setSelectedLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleClearDebt = async (lessonId = null) => {
    setLoading(true);
    setError("");
    try {
      const url = lessonId
        ? `${process.env.REACT_APP_BACKEND_ADDRESS}/user/${user.user_id}/clear-debt/${lessonId}`
        : `${process.env.REACT_APP_BACKEND_ADDRESS}/user/${user.user_id}/clear-debt`;

      const headers = await getAuthHeaders();
      const response = await axios.post(url, {}, {
        headers,
      });

      if (response.status === 200) {
        setConfirmDialog({ open: false, lessonId: null, type: null });
        onDebtCleared();
      }
    } catch (err) {
      console.error("Failed to clear debt", err);
      if (err.response && (err.response.status === 403 || err.response.status === 401)) {
        setError("Brak uprawnień do wyczyszczenia długu");
      } else {
        setError("Nie udało się wyczyścić długu");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClearPartialDebt = async () => {
    if (selectedLessons.length === 0) {
      setError("Wybierz przynajmniej jedno zajęcie do wyczyszczenia");
      return;
    }

    setLoading(true);
    setError("");
    
    let successCount = 0;
    let failedCount = 0;
    
    try {
      const headers = await getAuthHeaders();
      
      // Clear each selected lesson individually
      for (const lessonId of selectedLessons) {
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_ADDRESS}/user/${user.user_id}/clear-debt/${lessonId}`,
            {},
            { headers }
          );
          successCount++;
        } catch (err) {
          console.error(`Failed to clear debt for lesson ${lessonId}`, err);
          failedCount++;
        }
      }
      
      if (successCount > 0) {
        setPartialDialog({ open: false });
        setSelectedLessons([]);
        onDebtCleared();
      }
      
      if (failedCount > 0) {
        setError(`Wyczyszczono ${successCount} zajęć, ${failedCount} nie udało się wyczyścić`);
      }
    } catch (err) {
      console.error("Failed to clear partial debt", err);
      if (err.response && (err.response.status === 403 || err.response.status === 401)) {
        setError("Brak uprawnień do wyczyszczenia długu");
      } else {
        setError("Nie udało się wyczyścić długu");
      }
    } finally {
      setLoading(false);
    }
  };

  const openConfirmDialog = (lessonId = null, type = "all") => {
    setConfirmDialog({ open: true, lessonId, type });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, lessonId: null, type: null });
  };

  const toggleLessonSelection = (lessonId) => {
    setSelectedLessons(prev => 
      prev.includes(lessonId) 
        ? prev.filter(id => id !== lessonId)
        : [...prev, lessonId]
    );
  };

  const selectAllLessons = () => {
    setSelectedLessons(user.lessons.map(lesson => lesson.lesson_id));
  };

  const deselectAllLessons = () => {
    setSelectedLessons([]);
  };

  const getSelectedTotal = () => {
    return user.lessons
      .filter(lesson => selectedLessons.includes(lesson.lesson_id))
      .reduce((sum, lesson) => sum + lesson.price, 0);
  };

  const openPartialDialog = () => {
    setPartialDialog({ open: true });
    setSelectedLessons([]);
    setError("");
  };

  const closePartialDialog = () => {
    setPartialDialog({ open: false });
    setSelectedLessons([]);
    setError("");
  };

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {user.full_name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {user.email}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Chip
            label={`${user.debt_count} zajęć`}
            color="warning"
            size="small"
          />
        </TableCell>
        <TableCell align="right">
          <Typography variant="body1" sx={{ fontWeight: 600, color: "error.main" }}>
            {user.debt_amount} PLN
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={openPartialDialog}
              disabled={loading}
            >
              Częściowo
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openConfirmDialog(null, "all")}
              disabled={loading}
              startIcon={<MoneyOffIcon />}
            >
              Wszystko
            </Button>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                Szczegóły długu
              </Typography>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Table size="small" aria-label="debt details">
                <TableHead>
                  <TableRow>
                    <TableCell>Nazwa zajęć</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Kwota</TableCell>
                    <TableCell align="right">Akcja</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {user.lessons.map((lesson) => (
                    <TableRow key={lesson.lesson_id}>
                      <TableCell>{lesson.lesson_name}</TableCell>
                      <TableCell>
                        {new Date(lesson.lesson_date).toLocaleDateString("pl-PL", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>
                      <TableCell align="right">{lesson.price} PLN</TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => openConfirmDialog(lesson.lesson_id, "single")}
                          disabled={loading}
                        >
                          Wyczyść
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">
          Potwierdzenie wyczyszczenia długu
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.type === "all"
              ? `Czy na pewno chcesz wyczyścić cały dług (${user.debt_amount} PLN) dla użytkownika ${user.full_name}?`
              : `Czy na pewno chcesz wyczyścić ten dług dla użytkownika ${user.full_name}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={loading}>
            Anuluj
          </Button>
          <Button
            onClick={() => handleClearDebt(confirmDialog.lessonId)}
            color="primary"
            variant="contained"
            disabled={loading}
            autoFocus
          >
            {loading ? <CircularProgress size={20} /> : "Wyczyść"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Partial Debt Clearing Dialog - Granular Selection */}
      <Dialog
        open={partialDialog.open}
        onClose={closePartialDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Wybierz zajęcia do wyczyszczenia</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Użytkownik: <strong>{user.full_name}</strong><br />
            Całkowity dług: <strong>{user.debt_amount} PLN</strong> ({user.debt_count} zajęć)
          </DialogContentText>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Selection Controls */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={selectAllLessons}
                disabled={loading}
                startIcon={<CheckBoxIcon />}
              >
                Zaznacz wszystkie
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={deselectAllLessons}
                disabled={loading}
                startIcon={<CheckBoxOutlineBlankIcon />}
              >
                Odznacz wszystkie
              </Button>
            </Box>
            {selectedLessons.length > 0 && (
              <Chip
                label={`Wybrano: ${selectedLessons.length} zajęć (${getSelectedTotal()} PLN)`}
                color="primary"
                size="small"
              />
            )}
          </Box>

          {/* Lessons Table */}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 50 }}></TableCell>
                  <TableCell>Nazwa zajęć</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell align="right">Kwota</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {user.lessons.map((lesson) => {
                  const isSelected = selectedLessons.includes(lesson.lesson_id);
                  return (
                    <TableRow
                      key={lesson.lesson_id}
                      hover
                      onClick={() => toggleLessonSelection(lesson.lesson_id)}
                      sx={{ 
                        cursor: 'pointer',
                        backgroundColor: isSelected ? 'action.selected' : 'inherit',
                        '&:hover': {
                          backgroundColor: isSelected ? 'action.selected' : 'action.hover',
                        }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleLessonSelection(lesson.lesson_id);
                          }}
                        >
                          {isSelected ? (
                            <CheckBoxIcon color="primary" />
                          ) : (
                            <CheckBoxOutlineBlankIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{lesson.lesson_name}</TableCell>
                      <TableCell>
                        {new Date(lesson.lesson_date).toLocaleDateString("pl-PL", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{lesson.price} PLN</strong>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Kliknij na wiersz lub checkbox, aby zaznaczyć/odznaczyć zajęcia do wyczyszczenia.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={closePartialDialog} 
            disabled={loading}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleClearPartialDebt}
            color="primary"
            variant="contained"
            disabled={loading || selectedLessons.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : <MoneyOffIcon />}
          >
            {loading ? "Czyszczenie..." : `Wyczyść wybrane (${selectedLessons.length})`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const DebtManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchUsersWithDebt = async () => {
    setLoading(true);
    setError("");
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user/debt-management/`,
        {
          headers,
        }
      );

      if (response.status === 200 && response.data.users) {
        setUsers(response.data.users);
        setFilteredUsers(response.data.users);
      }
    } catch (err) {
      console.error("Failed to fetch users with debt", err);
      if (err.response && (err.response.status === 403 || err.response.status === 401)) {
        setError("Brak uprawnień do pobrania użytkowników z długiem");
      } else {
        setError("Nie udało się załadować użytkowników z długiem");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersWithDebt();
  }, []);

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(
        (user) =>
          user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const handleDebtCleared = () => {
    fetchUsersWithDebt();
  };

  const totalDebt = users.reduce((sum, user) => sum + user.debt_amount, 0);
  const totalUsers = users.length;

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          Zarządzanie długami użytkowników
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchUsersWithDebt}
          disabled={loading}
        >
          Odśwież
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Paper sx={{ p: 2, flex: "1 1 200px", minWidth: 200 }}>
          <Typography variant="body2" color="textSecondary">
            Użytkownicy z długiem
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "warning.main" }}>
            {totalUsers}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: "1 1 200px", minWidth: 200 }}>
          <Typography variant="body2" color="textSecondary">
            Całkowity dług
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "error.main" }}>
            {totalDebt} PLN
          </Typography>
        </Paper>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Szukaj użytkownika po nazwisku lub emailu..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && filteredUsers.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {searchTerm
            ? "Nie znaleziono użytkowników pasujących do wyszukiwania"
            : "Nie ma użytkowników z długiem. Świetnie!"}
        </Alert>
      )}

      {!loading && !error && filteredUsers.length > 0 && (
        <TableContainer component={Paper}>
          <Table aria-label="debt management table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Użytkownik</TableCell>
                <TableCell align="right">Liczba zajęć</TableCell>
                <TableCell align="right">Kwota długu</TableCell>
                <TableCell align="right">Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <Row
                  key={user.user_id}
                  user={user}
                  onDebtCleared={handleDebtCleared}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default DebtManagement;
